import { FORM_IDS } from '@bulknutrients/bulk-shared-lib/constants';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button, ButtonType } from '@components/Button';
import { Container } from '@components/Container';
import { Input, Textarea } from '@components/FormComponents';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useLogout } from '@hooks/customer/useLogout';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

export default function ContactUsPage(): ReactElement {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [success, setSuccess] = useState(false);

  const logout = useLogout();
  const { customer } = useCustomer();
  const formMethods = useForm<any>({
    mode: 'onTouched',
  });

  const handleLogout = async () => {
    await logout();
  };

  useEffect(() => {
    if (customer) {
      // Pre-fill the form with customer data
      formMethods.reset({
        first_name: customer.firstName || '',
        last_name: customer.lastName || '',
        phone_number: customer.phone || '',
        email: customer.email || '',
      });
    } else {
      // Reset the form if no customer data is available
      formMethods.reset({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
      });
    }
  }, [customer, formMethods.reset]);

  const onSubmit: SubmitHandler<any> = async (data) => {
    setSubmitting(true);
    setSuccess(false);

    // Save the form data
    const response = await fetch('/api/form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify({
        formId: FORM_IDS.CONTACT_US,
        formData: data,
        uniqueEntry: false,
        emails: [
          {
            recipient: 'cs',
            postmarkTemplateId: 34803275,
            replyToFormField: true,
          },
        ],
      }),
    });
    if (response.ok) {
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      // Only reset after a successful submission
      formMethods.reset();
    } else {
      setSubmitError(true);
    }
    setSubmitting(false);
  };

  return (
    <>
      <Head>
        <title>Contact Bulk Nutrients via email, phone or live chat</title>
        <meta
          name="title"
          content="Contact Bulk Nutrients via email, phone or live chat"
        />
        <meta
          name="description"
          content="Get in contact Bulk Nutrients in Hobart, Tasmania via phone, email or live chat for pure protein powders, supplement/nutritional advice and more!"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/contact-us`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Contact Us', path: '/contact-us' },
          ]}
        />
      </Container>
      <main id="main-content">
        <Container
          as="article"
          className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
        >
          <h1>Contact Bulk Nutrients</h1>

          <Paragraph>
            <b>Bulk Nutrients Postal Address:</b> 7 Crabtree Road, Grove,
            Tasmania, 7109
          </Paragraph>
          <Paragraph>
            <b>Call Bulk Nutrients:</b>{' '}
            <TextLink href="tel:+61362664725">03 6266 4725</TextLink>
          </Paragraph>
          <Paragraph>
            We get that sometimes you need to speak to someone to get your
            question answered, and that's why we're available on phones from
            8:30am to 5pm Monday to Friday (
            {new Date()
              .toLocaleString('en-AU', {
                timeZone: 'Australia/Hobart',
                timeZoneName: 'long',
              })
              .includes('Daylight')
              ? 'AEDT'
              : 'AEST'}
            ).
          </Paragraph>
          <Paragraph>
            Give us a call on{' '}
            <TextLink href="tel:+61362664725">03 6266 4725</TextLink> and we'll
            help in any way we can.
          </Paragraph>
          <HorizontalDivider />
          <h2 className="italic has-[~figure]:inline-block has-[~figure]:w-fit">
            Get instant answers with Bulk Nutrients Live Chat
          </h2>
          <Paragraph>
            Timing is everything... which is why we have a Live Chat function.
          </Paragraph>
          <Paragraph>
            When it's enabled (normally during our business hours and of a
            weekday evening) you'll notice a 'Chat' button in the bottom right
            hand corner of each page. Hit this button to get your question
            answered in no time!
          </Paragraph>
          <HorizontalDivider />
          <h2 className="italic has-[~figure]:inline-block has-[~figure]:w-fit">
            Frequently Asked Questions
          </h2>
          <Paragraph>
            Sometimes the question you're going to ask will be covered on one of
            our detailed Frequently Asked Questions pages.
          </Paragraph>
          <Paragraph>Instant information! How good is that?!?</Paragraph>
          <div>
            <ul className="m-0 mb-5 ml-5 list-disc columns-1 p-0 pl-5">
              <li>
                <TextLink href="/faqs/shipping">Shipping FAQs</TextLink>
              </li>
              <li>
                <TextLink href="/contact-us/returns">Returns FAQs</TextLink>
              </li>
              <li>
                <TextLink href="/faqs">Product Category FAQs</TextLink>
              </li>
            </ul>
          </div>
          <HorizontalDivider />
          <h2 className="italic has-[~figure]:inline-block has-[~figure]:w-fit">
            Contact Bulk Nutrients Online
          </h2>
          <Paragraph>
            We have several specialised contact forms available. Using one of
            the following forms ensures your requests get to the right person
            quickly.
          </Paragraph>
          <div>
            <ul className="m-0 mb-5 ml-5 list-disc columns-1 p-0 pl-5">
              <li>
                <TextLink href="/contact-us/returns">
                  For item returns, broken bags on delivery or warranty issues
                </TextLink>
              </li>
              <li>
                <TextLink href="/contact-us/ambassador">
                  Find out about our Ambassador Program and apply online
                </TextLink>
              </li>
              <li>
                <TextLink href="/contact-us/event-support">
                  Event Support enquiry to see if Bulk Nutrients will support
                  your event
                </TextLink>
              </li>
            </ul>
          </div>
          <Paragraph>
            For all other enquiries, please use the contact form below.
          </Paragraph>
          <HorizontalDivider />
          <h2 className="italic has-[~figure]:inline-block has-[~figure]:w-fit mb-4">
            Bulk Nutrients General Contact Form
          </h2>
          <FormProvider {...formMethods}>
            <form
              onSubmit={formMethods.handleSubmit(onSubmit)}
              data-form-id={FORM_IDS.CONTACT_US}
              className="internalForm mx-auto my-0 flex max-w-[760px] flex-col gap-5"
            >
              {customer ? (
                <>
                  <Paragraph>
                    You're contacting Bulk Nutrients as {customer.firstName}{' '}
                    {customer.lastName} ({customer.email}). Not the correct
                    account?{' '}
                    <Button
                      buttonStyle="text"
                      className="inline"
                      onClick={handleLogout}
                    >
                      Log out
                    </Button>
                  </Paragraph>
                  <Textarea
                    label="Comments/Questions"
                    name="comments"
                    required
                    autoComplete="off"
                  />
                </>
              ) : (
                <>
                  <Input
                    label="First Name"
                    name="first_name"
                    type="text"
                    required
                    autoComplete="given-name"
                  />
                  <Input
                    label="Last Name"
                    name="last_name"
                    type="text"
                    required
                    autoComplete="family-name"
                  />
                  <Input
                    label="Phone Number"
                    name="phone_number"
                    type="tel"
                    required
                    autoComplete="phone"
                  />
                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                  />
                  <Textarea
                    label="Comments/Questions"
                    name="comments"
                    required
                    autoComplete="off"
                  />
                </>
              )}
              <Button type={ButtonType.submit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
              <SuccessAlert
                title=""
                message="Thanks for your submission!"
                show={success}
                setShow={setSuccess}
              />
              <ErrorAlert
                show={submitError}
                setShow={setSubmitError}
                title="Oh no!"
              >
                Uh oh! Something went wrong trying to save that. Give it another
                try, or use one of the other contact methods on this page if it
                still doesn't work.
              </ErrorAlert>
            </form>
          </FormProvider>
        </Container>
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  return { props: {} };
};

ContactUsPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
